import React, { useState } from 'react';
import { BarChart3, FileText, Calculator, User, Settings, LogOut } from 'lucide-react';
import { User as UserType, UserSettings } from '../types';
import { logout } from '../services/api';
import UserSettingsComponent from './UserSettings';

interface HeaderProps {
    user: UserType | null;
    userSettings: UserSettings | null;
    onLogout: () => void;
    onSettingsUpdate: (settings: UserSettings) => void;
}

const Header: React.FC<HeaderProps> = ({ user, userSettings, onLogout, onSettingsUpdate }) => {
    const [showSettings, setShowSettings] = useState(false);

    const handleLogout = () => {
        logout();
        onLogout();
    };

    const handleSettingsUpdate = (settings: UserSettings) => {
        onSettingsUpdate(settings);
        setShowSettings(false);
    };

    return (
        <>
            <header className="bg-white shadow-sm border-b">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <BarChart3 className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">
                                    Client Management System
                                </h1>
                                <p className="text-sm text-gray-600">
                                    Financial Analysis & Credit Scoring
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                    <FileText className="w-4 h-4" />
                                    <span>Upload Documents</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Calculator className="w-4 h-4" />
                                    <span>Get Analysis</span>
                                </div>
                            </div>

                            {user && (
                                <div className="flex items-center gap-3 ml-4 pl-4 border-l border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm font-medium text-gray-700">
                                            {user.username}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setShowSettings(true)}
                                            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                                            title="Settings"
                                        >
                                            <Settings className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={handleLogout}
                                            className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                                            title="Logout"
                                        >
                                            <LogOut className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {showSettings && (
                <UserSettingsComponent
                    onClose={() => setShowSettings(false)}
                    onSettingsUpdate={handleSettingsUpdate}
                />
            )}
        </>
    );
};

export default Header;
